import type { SessionInfo } from "@/api/generated/SessionInfo"

/**
 * Capabilities used to gate navigation and routes.
 * - portal permissions come from the membership in the active organization
 * - `admin`: owner/admin role in the active organization (or platform admin)
 * - `orgAdmin`: owner/admin role plus the management permission (member administration)
 * - `platformAdmin`: platform-wide superuser
 * - `any`: every signed-in user
 */
export type Capability =
  | "management"
  | "chat"
  | "workplace"
  | "tasks"
  | "admin"
  | "orgAdmin"
  | "platformAdmin"
  | "any"

export function can(session: SessionInfo | undefined, cap: Capability | undefined): boolean {
  if (!session?.authenticated) return false
  if (!cap || cap === "any") return true
  const isPlatformAdmin = !!session.user?.is_platform_admin
  switch (cap) {
    case "platformAdmin":
      return isPlatformAdmin
    case "admin":
      return isPlatformAdmin || session.role === "owner" || session.role === "admin"
    case "orgAdmin":
      return can(session, "admin") && can(session, "management")
    default:
      return !!session.permissions[cap]
  }
}

/** Landing page for a user, in priority order of what they may use. */
export function homePath(session: SessionInfo): string {
  if (can(session, "chat")) return "/chat"
  if (can(session, "workplace")) return "/workplace"
  if (can(session, "tasks")) return "/tasks"
  if (can(session, "management")) return "/studio/agents"
  return "/settings/profile"
}
